import DataType from '@linc-technologies/linced-common/enums/data-type';
import {
  getValueDefault,
  getValueKey,
} from '@linc-technologies/linced-common/utils/dynamic-field-types';

// A dynamic field value as the API sends it in the fields map. The map key is
// the field key.
export type FieldHash = Record<string, unknown> & {
  datatype?: number;
  key?: string;
};

// A dynamic field key has a namespace: `namespace:key`.
const FIELD_KEY = /^.+?:.+?$/;

// normalizeFields changes the API fields map to the fieldsArray of a record.
// Go omits zero values, so a value that is not in the payload gets the zero
// value of its datatype. A VALUE_LIST value gets the fragment default ([]).
export function normalizeFields(
  fields: Record<string, FieldHash> | null | undefined,
): FieldHash[] {
  const fieldsArray: FieldHash[] = [];
  if (!fields) {
    return fieldsArray;
  }

  for (const [key, field] of Object.entries(fields)) {
    const valueKey = getValueKey(field.datatype);
    if (!Object.hasOwn(field, valueKey)) {
      if (field.datatype !== DataType.VALUE_LIST) {
        field[valueKey] = getValueDefault(field.datatype);
      }
    } else if (field.datatype === DataType.INTEGER) {
      // The API can send an integer as a string.
      field['valueInt'] = parseInt(String(field['valueInt']), 10);
    }
    field.key = key;
    fieldsArray.push(field);
  }
  return fieldsArray;
}

// The part of a dynamic-field/value fragment that serializeFields reads.
export interface SerializableField {
  key: string | undefined;
  serialize(): Record<string, unknown>;
}

// serializeFields changes the fieldsArray of a record to the API fields map.
// It sends the datatype and the value only, and drops a field whose key has
// no namespace.
export function serializeFields(
  fieldsArray: Iterable<SerializableField>,
): Record<string, Record<string, unknown>> {
  const fields: Record<string, Record<string, unknown>> = {};
  for (const fragment of fieldsArray) {
    const key = fragment.key;
    if (!key || !FIELD_KEY.test(key)) {
      continue;
    }
    const field = fragment.serialize();
    const valueKey = getValueKey(field['datatype'] as number);
    fields[key] = {
      datatype: field['datatype'],
      [valueKey]: field[valueKey],
    };
  }
  return fields;
}
